<!--@format-->
<template>
    <div class="document-template-widget">
        <div class="widget-header">
            <h3>{{ title }}</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <loading-card :is-loading="isLoading" />
        <div v-if="!isLoading" class="content-wrapper">
            <div class="widget-container">
                <starting-step
                    v-if="currentStep === 'startingPoint'"
                    :template-options="startingOptions"
                    :selected-template-option="selectedStartingOption"
                    @input="handleInputStartingOption"
                />
                <existing-template-step
                    v-if="currentStep === 'existingTemplate'"
                    :form="form"
                    @item-preview:click="handleOpenPreview"
                />
                <document-settings-step
                    v-if="currentStep === 'settings'"
                    :form="form"
                    :v="$v"
                    type="document-template"
                />
                <document-upload-pdf-step
                    v-if="currentStep === 'uploadFile'"
                    :list-pdf-pages="pdfImagePages"
                    @get-pdf="handlePdf"
                    @get-pdf-collateral="handlePdfCollateral"
                />
            </div>
        </div>
        <div v-if="currentStep !== 'existingTemplate'" class="widget-footer">
            <div class="actions-container">
                <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
            </div>
            <div class="actions-container">
                <app-button
                    v-if="hasBackAction && !isEditMode"
                    class="secondary-btn"
                    @click="goBack"
                >
                    <span class="fa fa-arrow-left"></span>
                    {{ backLabel }}
                </app-button>
                <app-button
                    :disabled="
                        (currentStep === 'settings' || currentStep === 'existingTemplate') &&
                        !isFormValid
                    "
                    @click="goNext"
                >
                    {{ nextLabel }}
                    <span class="fa fa-arrow-right"></span>
                </app-button>
            </div>
        </div>
        <div v-else class="widget-footer existing-template">
            <div class="actions-container">
                <app-button v-if="hasBackAction" class="secondary-btn" @click="goBack">
                    {{ backLabel }}
                </app-button>

                <div class="add-button-container">
                    <div v-if="templatesCount" class="template-count">
                        <span>
                            {{ templatesCount }}
                            {{ templatesCount === 1 ? 'Template' : 'Templates' }} Selected
                        </span>
                    </div>
                    <app-button :disabled="noTemplatesAdded" class="add-btn" @click="goNext">
                        {{ nextLabel }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const DocumentExistingTemplateStep = () =>
    import('@/components/sliding-widgets/document/steps/document-existing-template-step.vue');
const DocumentSettingsStep = () =>
    import('@/components/sliding-widgets/document/steps/document-settings-step.vue');
const DocumentUploadPdfStep = () =>
    import('@/components/sliding-widgets/document/steps/document-upload-pdf-step.vue');
const LoadingCard = () => import('@/components/common/loading-card');
const StartingStep = () =>
    import('@/components/sliding-widgets/landing-page-template-widget/starting-step.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');

import EventBus from '@/scripts/event-bus';
import BrandingMixin from '@/mixins/branding-mixin';
import DocumentMixin from '@/mixins/document-mixin';
import DocumentBuilderMixin from '@/mixins/builder/document-builder-mixin';
import { DocumentService } from '@/scripts/services/document/document-service';
import { DocumentTemplateService } from '@/scripts/services/document/document-template-service';
import { required } from 'vuelidate/lib/validators';
import { intersectionBy, isNil, isEmpty } from 'lodash';

export default {
    name: 'document-template-widget',

    components: {
        'app-button': AppButton,
        'document-settings-step': DocumentSettingsStep,
        'existing-template-step': DocumentExistingTemplateStep,
        'document-upload-pdf-step': DocumentUploadPdfStep,

        'loading-card': LoadingCard,
        'starting-step': StartingStep,
        'widget-close-svg': WidgetCloseSvg,
    },

    mixins: [BrandingMixin, DocumentMixin, DocumentBuilderMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,
            selectedStartingOption: null,
            lastActiveStep: null,

            defaultForm: {
                name: null,
                categories: [],
                addedTemplates: [],
                isPublished: false,
                config: '',

                updatedDate: null,
                createdDate: null,
                publishedDate: null,
            },

            form: {
                name: null,
                categories: [],
                addedTemplates: [],
                isPublished: false,
                config: '',

                updatedDate: null,
                createdDate: null,
                publishedDate: null,
            },

            pdfImagePages: [],

            steps: {
                startingPoint: {
                    title: 'Create a Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.goToStep(this.selectedStartingOption.value);
                            },
                        },
                    },
                },

                existingTemplate: {
                    title: 'Choose templates to add',
                    actions: {
                        nextAction: {
                            label: 'Add',
                            callback: () => {
                                this.injectTemplates();
                            },
                        },

                        backAction: {
                            label: 'Cancel',
                            callback: () => {
                                this.form = {
                                    ...this.defaultForm,
                                };
                                this.goToStep('startingPoint');
                            },
                        },
                    },
                },

                settings: {
                    title: 'Create a Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.isEditMode
                                    ? this.patchDocumentTemplate()
                                    : this.createDocumentTemplate();
                            },
                        },

                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.form = {
                                    ...this.defaultForm,
                                };
                                this.goToStep(this.lastActiveStep);
                            },
                        },
                    },
                },

                uploadFile: {
                    title: 'Create a New Document',
                    actions: {
                        nextAction: {
                            label: 'Next',
                            callback: () => {
                                this.injectPagesFromPdf();
                            },
                        },

                        backAction: {
                            label: 'Cancel',
                            callback: () => {
                                this.form = {
                                    ...this.defaultForm,
                                };
                                this.goToStep('startingPoint');
                            },
                        },
                    },
                },
            },

            currentStep: 'startingPoint',
        };
    },

    validations: {
        form: {
            name: {
                required,
            },
        },
    },

    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },

        title() {
            if (this.createFromDocument) {
                return 'Save as Template';
            } else if (this.isEditMode) {
                return 'Edit Template';
            } else {
                return this.steps[this.currentStep].title;
            }
        },

        widgetType() {
            return this.widget.options.type;
        },

        isEditMode() {
            return this.widgetType === 'template' && !isNil(this.widget.options.documentTemplateId);
        },

        isEditFromBuilder() {
            return this.widget.options.builder;
        },

        createFromDocument() {
            return this.widgetType === 'document' && this.widget.options.documentId;
        },

        isFormValid() {
            return !this.$v.$invalid;
        },

        templatesCount() {
            return this.form.addedTemplates.length;
        },

        noTemplatesAdded() {
            return isEmpty(this.form.addedTemplates);
        },

        baseUrl() {
            return this.$store.getters.baseUrl;
        },

        startingOptions() {
            const options = [
                {
                    imageUrl: require('@/assets/images/document-builder-fresh.png'),
                    name: 'Start Fresh',
                    value: 'settings',
                },
                {
                    imageUrl: require('@/assets/images/existing-document-template.png'),
                    name: 'Existing Template',
                    value: 'existingTemplate',
                },
                {
                    imageUrl: require('@/assets/images/upload-pdf.png'),
                    name: 'Upload File',
                    value: 'uploadFile',
                },
            ];

            return options;
        },

        hasBackAction() {
            return (
                this.steps[this.currentStep].actions.backAction !== undefined &&
                !this.isEditMode &&
                !this.createFromDocument
            );
        },

        goBack() {
            return this.steps[this.currentStep].actions.backAction.callback;
        },

        goNext() {
            return this.steps[this.currentStep].actions.nextAction.callback;
        },

        backLabel() {
            return this.steps[this.currentStep].actions.backAction.label;
        },

        nextLabel() {
            return this.steps[this.currentStep].actions.nextAction.label;
        },

        isFromStudio() {
            return this.widget.options.isFromStudio;
        },
    },

    created() {
        if (this.isEditMode) {
            this.goToStep('settings');
            this.getEditDocumentTemplateData();
            return;
        }

        if (this.createFromDocument) {
            this.goToStep('settings');
            this.setDocumentData(this.widget.options.documentId);
            return;
        }
        this.isLoading = false;
        this.selectedStartingOption = this.startingOptions[0];
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        goToStep(step) {
            this.lastActiveStep = this.currentStep;
            this.currentStep = step;
        },

        handleInputStartingOption(newOptionSelected) {
            this.selectedStartingOption = newOptionSelected;
        },

        cloneConfigPages(config) {
            if (config) {
                const { pages } = JSON.parse(config);
                return pages.map((page) => this.clonePage(page));
            } else {
                return [];
            }
        },

        injectTemplates() {
            let addedPages = [];
            this.isLoading = true;
            const promises = this.form.addedTemplates.map(({ id }) => {
                const config = {
                    select: 'Id,Name,TemplateJson',
                };
                return new Promise((resolve, reject) => {
                    DocumentTemplateService.get(id, config)
                        .then(({ data: { value } }) => {
                            resolve(value[0]);
                        })
                        .catch((error) => {
                            reject(error);
                        });
                });
            });

            Promise.all(promises)
                .then((results) => {
                    results.forEach(({ TemplateJson }) => {
                        const clonedPages = this.cloneConfigPages(TemplateJson);
                        addedPages = [...addedPages, ...clonedPages];
                    });
                    this.form.config = JSON.stringify({ pages: [...addedPages], global: {} });
                    this.goToStep('settings');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error ocurred while adding your templates.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        injectPagesFromPdf() {
            const pages = [];
            this.pdfImagePages.forEach((pdfPage) => {
                if (pdfPage.selected) {
                    pages.push(this.createNewFreeFormPage({}, pdfPage.img));
                }
            });

            this.form.config = JSON.stringify({ pages: [...pages], global: {} });
            this.goToStep('settings');
        },

        handlePdf(pdfFile) {
            this.isLoading = true;
            const fd = this.createFormForPdfFile(pdfFile);
            DocumentService.postPdfFormData(fd)
                .then(({ data }) => {
                    let actualLength = this.pdfImagePages.length;
                    data.data.forEach((img, index) => {
                        this.pdfImagePages.push({
                            id: actualLength + (index + 1),
                            img: img,
                            selected: true,
                            order: actualLength + (index + 1),
                        });
                    });
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error ocurred while trying to upload your pdf.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        handlePdfCollateral(urlPdf) {
            this.isLoading = true;
            const payload = {
                url: urlPdf,
            };

            DocumentService.postPdfFromCollateral(payload)
                .then(({ data }) => {
                    let actualLength = this.pdfImagePages.length;
                    data.data.forEach((img, index) => {
                        this.pdfImagePages.push({
                            id: actualLength + (index + 1),
                            img: img,
                            selected: true,
                            order: actualLength + (index + 1),
                        });
                    });
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error ocurred while trying to get your pdf.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        // Create
        createDocumentTemplate() {
            this.isLoading = true;

            this.submitDocumentTemplate()
                .then((Id) => {
                    this.closeWidget();
                    if (!this.createFromDocument) {
                        this.$router.push(`${this.baseUrl}/Documents/Templates/${Id}/Design`);
                    }
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while creating your template',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                    EventBus.$emit('document-template:update');
                });
        },

        submitDocumentTemplate() {
            return new Promise((resolve, reject) => {
                const mappedCategories = this.form.categories.map((name) => ({
                    Category: name,
                }));

                const payload = {
                    Name: this.form.name,
                    Categories: mappedCategories,
                    TemplateJson: this.form.config,
                    AccountId: this.accountId,
                    HasBranding: this.form.config
                        ? this.hasDocumentBranding(JSON.parse(this.form.config))
                        : false,
                };

                if (this.createFromDocument) {
                    payload.DocumentId = this.widget.options.documentId;
                }

                DocumentTemplateService.post(payload)
                    .then(({ data: { Id } }) => {
                        this.$helpers.showSuccessNotification('Template created successfully.');
                        resolve(Id);
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },

        // Edit
        getEditDocumentTemplateData() {
            return new Promise((resolve, reject) => {
                this.isLoading = true;
                const { documentTemplateId } = this.widget.options;

                const config = {
                    expand: 'Categories($select=Id,Category)',
                };

                DocumentTemplateService.get(documentTemplateId, config)
                    .then((response) => {
                        this.setupDocumentTemplateInformation(response, true);
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to get template info', {
                            error,
                        });
                        reject();
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            });
        },

        setDocumentData(documentId) {
            DocumentService.get(documentId, {
                select: 'Name,Categories,TemplateJson',
                expand: 'Categories',
            })
                .then((response) => {
                    const document = response.data.value[0];
                    const clonedPages = this.cloneConfigPages(document.TemplateJson);
                    const clonedConfig = JSON.stringify({
                        pages: clonedPages,
                        global: {},
                        totalPages: clonedPages.length,
                    });

                    this.form = {
                        ...this.form,
                        name: document.Name,
                        categories: document.Categories.map(({ Category }) => Category),
                        config: clonedConfig,
                    };
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting your document',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        // Update
        patchDocumentTemplate() {
            this.isLoading = true;

            const { documentTemplateId } = this.widget.options;

            const mappedCategories = this.form.categories.map((name) => ({
                Category: name,
                DocumentTemplateId: documentTemplateId,
            }));

            const changes = [
                {
                    name: 'Name',
                    value: this.form.name,
                },
                {
                    name: 'Categories',
                    value: mappedCategories,
                },
                {
                    name: 'TemplateJson',
                    value: this.form.config,
                },
                {
                    name: 'HasBranding',
                    value: this.form.config
                        ? this.hasDocumentBranding(JSON.parse(this.form.config))
                        : false,
                },
            ];

            if (this.isEditFromBuilder) {
                changes.splice(2, 1);
            }

            DocumentTemplateService.patch(documentTemplateId, changes)
                .then(({ data }) => {
                    this.$helpers.showSuccessNotification(
                        'Document template updated successfully.',
                    );
                    if (this.isEditFromBuilder) {
                        EventBus.$emit('document:update-info', data);
                    } else {
                        const dest =
                            `${this.baseUrl}/Documents/Templates/${documentTemplateId}/Design` +
                            `${this.isFromStudio ? '?fromStudio=true' : ''}`;
                        EventBus.$emit('document-template:update');

                        this.$router.push(dest);
                    }
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to update the document template.', {
                        error,
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        handleOpenPreview({ id }) {
            const route = this.$router.resolve({
                path: `${this.baseUrl}/Document/Template/${id}/Preview`,
            });
            window.open(route.href);
        },
    },
};
</script>

<style>
.document-template-widget.widget .content-wrapper {
    padding: 40px 24px;
}

.document-template-widget .starting-step .app-input-radio.options {
    gap: 24px;
    justify-content: space-between;
    padding: 30px 45px 30px 45px;
}

.document-template-widget .starting-step .app-input-radio.horizontal .option-image {
    height: 157px;
    width: 258px;
}

.document-template-widget
    .starting-step
    .app-input-radio
    .radio-option-label-container
    .circle::before {
    height: 18px;
    width: 18px;
}

.document-template-widget .widget-footer .transparent-btn {
    background: transparent;
    border: 1px solid #cac6cb;
    color: #382153;
}

.document-template-widget .widget-footer .transparent-btn:hover {
    background: #e1e0e5;
}

.document-template-widget .widget-footer .actions-container {
    display: flex;
    justify-content: space-between;
}

.document-template-widget .widget-footer.existing-template .actions-container {
    width: 100%;
}

.document-template-widget .widget-footer.existing-template .add-button-container {
    align-items: center;
    display: flex;
    gap: 15px;
}

.document-template-widget .widget-footer.existing-template .template-count {
    color: #382153;
    font: 500 14px 'Roboto';
}

.document-template-widget .widget-footer.existing-template .add-btn {
    padding: 0 40px;
}

.document-template-widget .widget-footer .secondary-btn {
    background: transparent;
    border: 1px solid#18aeff;
    color: #18aeff;
    margin-right: 14px;
}

.document-template-widget .widget-footer .secondary-btn:hover {
    background: #dbedfa;
}

.document-template-widget .widget-footer .secondary-btn span.fa {
    margin-right: 10px;
}

.document-template-widget .widget-footer .app-button:not(.secondary-btn) span.fa {
    margin-left: 10px;
}
</style>
